import {
  ScheduleModel,
  SchoolSportSeason
} from '@maxpreps/apiserviceclients/apiModels';
import TeamLevel from '@maxpreps/shared/constants/teamLevel';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import {
  IPickerModalProps,
  PickerImageType
} from '@maxpreps/shared/corecomponents/PickerModal/PickerModal';
import { push as routerPush } from '@maxpreps/shared/functions/primitiveHelpers/routerHelpers';
import { resizeMascotImage } from '@maxpreps/shared/functions/utils/resizeImage';
import {
  filterByLevel,
  getLevels
} from '@maxpreps/shared/functions/utils/sportSeasonUtils';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { ISelectProps } from '@maxpreps/shared/ui/Select/Select';
import { useCallback, useMemo, useState } from 'react';
import getCSRRewriteAttributes from '../../../utils/getCSRRewriteAttributes';
import useIsUserATeamAdmin from '../../../utils/useIsUserATeamAdmin';
import { getTeamUrl } from './../../../utils/navigation';
import { Page } from './../../../utils/pages';

const getInitSeasonIndex = (
  seasons: SchoolSportSeason[],
  ssid: string
): number => seasons.findIndex(s => s.sportSeasonId === ssid) || 0;

const getInitLevelIndex = (levels: string[], level: string): number =>
  levels.findIndex(l => l === level) || 0;

const findSportSeason = (
  sportSeasons: SchoolSportSeason[],
  sportSeasonId: string
): SchoolSportSeason | undefined => {
  if (!sportSeasonId) return;

  return sportSeasons?.find(ss => ss.sportSeasonId === sportSeasonId);
};

const useSeasonPickerProps = (): Omit<IPickerModalProps, 'onClose'> => {
  const teamContext = useTeamContext();
  const isAdminPage = useIsAdmin();
  const isUserAVarsityAdmin = useIsUserATeamAdmin(TeamLevel.Varsity);
  const { appType } = useRequestContext();
  const isAdmin = useIsAdmin();

  const { data, teamSeasonPickerData, sportData } = teamContext;
  const {
    level,
    schoolMascotUrl,
    schoolName,
    schoolCity,
    stateCode,
    sportSeasonId,
    teamId
  } = data;

  const { scheduleModel: scheduleModelKey } = sportData as never as {
    scheduleModel: keyof ScheduleModel;
  };

  const filteredTeamSeasonPickerData = teamSeasonPickerData.filter(
    ss => ss.isPublished || isAdmin
  );

  // Level data
  const levels = useMemo(
    () => getLevels(filteredTeamSeasonPickerData),
    [filteredTeamSeasonPickerData]
  );

  const [levelIndex, setLevelIndex] = useState(
    getInitLevelIndex(levels, level)
  );

  const levelValue = levels[levelIndex];

  // Season data
  const seasons = useMemo(
    () => filterByLevel(filteredTeamSeasonPickerData, levelValue),
    [filteredTeamSeasonPickerData, levelValue]
  );

  const [seasonIndex, setSeasonIndex] = useState(
    getInitSeasonIndex(seasons, sportSeasonId)
  );

  const ssid = seasons[seasonIndex]?.sportSeasonId;

  // Level config
  const onLevelChange = useCallback(e => {
    setLevelIndex(e.target.selectedIndex);
    setSeasonIndex(0);
  }, []);

  const onSeasonChange = useCallback(e => {
    setSeasonIndex(e.target.selectedIndex);
  }, []);

  const levelConfig: ISelectProps = {
    name: 'Level',
    value: levelValue,
    options: levels.map(l => ({ value: l, label: l })),
    onChange: onLevelChange
  };

  const seasonConfig: ISelectProps = {
    name: 'Season',
    value: seasons[seasonIndex]?.sportSeasonId,
    options: seasons.map(s => ({
      value: s.sportSeasonId,
      label: `${s.season} ${s.year}`
    })),
    onChange: onSeasonChange
  };

  const dropdownConfig = [seasonConfig];

  if (!isAdminPage || isUserAVarsityAdmin) {
    dropdownConfig.unshift(levelConfig);
  }

  const onSubmit = useCallback(() => {
    var ss = findSportSeason(filteredTeamSeasonPickerData, ssid);

    if (!ss) return;

    // Bug? What happens when a JV coach in his admin (JV admin) tries to go to a varsity team?
    const anchorProps = getCSRRewriteAttributes(
      getTeamUrl(
        isAdminPage ? Page.AdminDashboard : Page.Home,
        ss.canonicalUrl,
        { ...(isAdminPage ? { admin: 1 } : undefined) }
      ),
      teamId,
      ss.sportSeasonId,
      ss.allSeasonId,
      appType,
      undefined,
      ScheduleModel[scheduleModelKey]
    );

    if (!anchorProps) {
      return;
    }

    if (!anchorProps.as) {
      window.location.href = anchorProps.href;
      return;
    }

    routerPush(anchorProps.href, anchorProps.as);
  }, [
    teamId,
    scheduleModelKey,
    ssid,
    isAdminPage,
    appType,
    filteredTeamSeasonPickerData
  ]);

  const imageUrl = resizeMascotImage(schoolMascotUrl, null, 128, 128);

  return {
    dropdownConfig,
    isSubmitEnabled: true,
    onSubmit,
    title: schoolName,
    subTitle: `${schoolCity}, ${stateCode}`,
    imageType: PickerImageType.Mascot,
    imageProps: {
      src: imageUrl
    }
  };
};

export default useSeasonPickerProps;
